// React Query hooks for payment schedule templates management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tuitionService from '@/services/tuitionService';
import type {
  PaymentScheduleQuery,
  CreatePaymentScheduleData,
  UpdatePaymentScheduleData,
  DuplicatePaymentScheduleData,
} from '@/types/tuition.types';

// ==================== Query Keys ====================

export const paymentScheduleKeys = {
  all: ['payment-schedules'] as const,
  lists: () => [...paymentScheduleKeys.all, 'list'] as const,
  list: (query: PaymentScheduleQuery) => [...paymentScheduleKeys.lists(), query] as const,
  details: () => [...paymentScheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentScheduleKeys.details(), id] as const,
  default: () => [...paymentScheduleKeys.all, 'default'] as const,
};

// ==================== Query Hooks ====================

/**
 * Fetch payment schedule templates with pagination and filtering
 */
export function usePaymentSchedules(query: PaymentScheduleQuery = {}) {
  return useQuery({
    queryKey: paymentScheduleKeys.list(query),
    queryFn: () => tuitionService.fetchPaymentSchedules(query),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single payment schedule template by ID
 */
export function usePaymentSchedule(id: string | null) {
  return useQuery({
    queryKey: paymentScheduleKeys.detail(id || ''),
    queryFn: () => tuitionService.fetchPaymentSchedule(id!),
    enabled: !!id,
  });
}

/**
 * Fetch the default payment schedule template
 */
export function useDefaultPaymentSchedule() {
  return useQuery({
    queryKey: paymentScheduleKeys.default(),
    queryFn: () => tuitionService.fetchDefaultPaymentSchedule(),
    staleTime: 60000, // 1 minute
  });
}

// ==================== Mutation Hooks ====================

/**
 * Create a new payment schedule template
 */
export function useCreatePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePaymentScheduleData) =>
      tuitionService.createPaymentSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
  });
}

/**
 * Update an existing payment schedule template
 */
export function useUpdatePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentScheduleData }) =>
      tuitionService.updatePaymentSchedule(id, data),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.detail(id) });
    },
  });
}

/**
 * Set a payment schedule as the default
 */
export function useSetDefaultPaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.setDefaultPaymentSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
  });
}

/**
 * Duplicate a payment schedule template
 */
export function useDuplicatePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DuplicatePaymentScheduleData }) =>
      tuitionService.duplicatePaymentSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
  });
}

/**
 * Archive a payment schedule template
 */
export function useArchivePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.archivePaymentSchedule(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.detail(id) });
    },
  });
}

/**
 * Restore an archived payment schedule template
 */
export function useRestorePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.restorePaymentSchedule(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.detail(id) });
    },
  });
}

/**
 * Delete a payment schedule template (only if not in use)
 */
export function useDeletePaymentSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.deletePaymentSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
  });
}
